'use client';

// useVisualViewportBox — the part of the layout viewport the reader can actually
// see (`window.visualViewport`'s offset + height), so a fixed full-height overlay
// can pin itself to it while a phone's soft keyboard is up (BUG-1840).
//
// iOS never resizes the layout viewport for the keyboard (it ignores
// `interactive-widget=resizes-content`): it shrinks the VISUAL viewport and pans
// it to bring the focused field into view. A `fixed inset-y-0` panel therefore
// stays full height behind the keyboard and its header pans off the top. CSS has
// no keyboard-aware unit on iOS (`dvh` tracks the toolbar, not the keyboard), so
// the box comes from here. On Android and desktop the visual viewport equals the
// layout viewport and pinning to it is a no-op.
//
// SHARED beside `useMediaQuery` (ADR 030 — Design Canon, Decision 2): viewport
// behavior with no appearance.

import { useCallback, useMemo, useSyncExternalStore } from 'react';

/** The visible box, in layout-viewport CSS pixels. */
export interface VisualViewportBox {
    /** Distance from the layout viewport's top to the visible area's top (the keyboard pan). */
    top: number;
    /** Height of the visible area (the screen minus the keyboard). */
    height: number;
}

/** Pinch-zoom leaves `scale` above 1; anything within this of 1 counts as unzoomed. */
const UNZOOMED_TOLERANCE = 0.01;

/** Snapshot for "nothing to follow": server, jsdom, disabled, or zoomed. */
const NO_BOX = '';
const readNoBox = (): string => NO_BOX;

/**
 * The current box as a primitive key, so `useSyncExternalStore` compares it by
 * value and a no-op viewport event causes no re-render.
 *
 * @returns `"<top>:<height>"`, or `NO_BOX` when there is no visual viewport or
 *   it is pinch-zoomed.
 */
function readBoxKey(): string {
    const vv = typeof window === 'undefined' ? null : window.visualViewport;
    // Pinch-zoom shrinks the visual viewport too. Following it would shrink and
    // slide the panel under the reader's fingers, so only the unzoomed
    // (keyboard) case is tracked.
    if (!vv || Math.abs(vv.scale - 1) > UNZOOMED_TOLERANCE) return NO_BOX;
    return `${vv.offsetTop}:${vv.height}`;
}

/**
 * Tracks the visible part of the viewport while `enabled`, re-rendering on
 * every `visualViewport` resize (keyboard opening/closing) and scroll (iOS
 * panning a focused field into view).
 *
 * Hydration-safe via `useSyncExternalStore`: SSR and the first client paint read
 * `null`, so a consumer falls back to its CSS box until the client reads the
 * real one.
 *
 * @param enabled - Subscribe only while true (e.g. while an overlay is open);
 *   `false` returns `null` and attaches no listeners.
 * @returns The visible box, or `null` when disabled, unsupported, or
 *   pinch-zoomed — callers keep their CSS geometry then.
 */
export function useVisualViewportBox(enabled: boolean): VisualViewportBox | null {
    const subscribe = useCallback(
        (onChange: () => void) => {
            const vv = enabled && typeof window !== 'undefined' ? window.visualViewport : null;
            if (!vv) return () => {};
            vv.addEventListener('resize', onChange);
            vv.addEventListener('scroll', onChange);
            return () => {
                vv.removeEventListener('resize', onChange);
                vv.removeEventListener('scroll', onChange);
            };
        },
        [enabled],
    );
    const key = useSyncExternalStore(subscribe, enabled ? readBoxKey : readNoBox, readNoBox);
    return useMemo(() => {
        if (key === NO_BOX) return null;
        const [top, height] = key.split(':').map(Number);
        return { top, height };
    }, [key]);
}
